import { Component, OnInit, inject, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { Campaign, CampaignAdminRequest, CampaignLocationFilter, CampaignsCampaignAdminApiClient, CampaignsCampaignLocationFilterAdminApiClient, StaffCampaignStrategy } from 'src/app/api/api-campaigns.service.generated';
import { MessageDialogModel } from 'src/app/common/message-dialog/message-dialog.component';
import { CampaignBaseEditComponent } from '../_/campaign-base-edit/campaign-base-edit.component';
import { CampaignLocationFiltersComponent } from '../_/campaign-location-filters/campaign-location-filters.component';
import { StaffCampaignStrategyComponent } from './staff-campaign-strategy/staff-campaign-strategy.component';
import { PERMISSION } from 'src/app/const/permission';
import { PageContainerComponent, BreadcrumbsComponent, ButtonComponent } from 'wecheer-common';
import { CampaignMediaComponent } from '../_/campaign-media/campaign-media.component';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { AsyncPipe, JsonPipe } from '@angular/common';
import { ScreenService } from 'src/app/common/services/screen.service';
import { AppVersionService } from 'src/app/common/services/app-version.service';
import { MatTabsModule } from '@angular/material/tabs';
import { CampaignCloneService } from 'src/app/common/services/campaign-clone.service';
@Component({
  selector: 'app-staff-campaign-edit',
  templateUrl: './staff-campaign-edit.component.html',
  styleUrls: ['./staff-campaign-edit.component.scss'],
  imports: [
    CampaignBaseEditComponent,
    StaffCampaignStrategyComponent,
    CampaignLocationFiltersComponent,
    CampaignMediaComponent,
    MatCardModule,
    MatIconModule,
    MatTabsModule,
    HasAnyPermissionDirective,
    JsonPipe,
    AsyncPipe,
    PageContainerComponent,
    BreadcrumbsComponent,
    ButtonComponent
  ]
})
export class StaffCampaignEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private campaignLocationFiltersApi = inject(CampaignsCampaignLocationFilterAdminApiClient);
  private campaignCloneService = inject(CampaignCloneService);
  screen = inject(ScreenService);
  appVersion = inject(AppVersionService);


  readonly campaignBase = viewChild<CampaignBaseEditComponent>('campaignBase');
  readonly campaignStrategy = viewChild<StaffCampaignStrategyComponent>('campaignStrategy');
  readonly campaignLocationFilters = viewChild<CampaignLocationFiltersComponent>('campaignLocationFilters');

  editModel: CampaignAdminRequest;

  campaign: Campaign;

  campaignReplay = new BehaviorSubject<Campaign>(undefined);

  campaignId: string;
  advertiserId: string;
  originalCampaignId: string;
  clonedLocationFilters: CampaignLocationFilter;

  isLoading: boolean;
  isAdding: boolean;
  isCloning: boolean;

  viewMode: 'scheme' | 'locations' | 'json' | 'media' | '' = 'scheme';
  readonly permissions = PERMISSION;


  constructor() {

    this.isAdding = this.route.snapshot.params.campaignId === 'new';
    this.isCloning = this.route.snapshot.queryParams.clone === 'true';
    this.campaignId = this.isAdding ? undefined : this.route.snapshot.params.campaignId;
    this.advertiserId = this.route.snapshot.params.advertiserId;
  }

  async ngOnInit() {
    try {
      this.isLoading = true;
      if (this.isCloning) {
        await this.cloneCampaign();
      } else {
        this.campaign = this.campaignId
          ? await lastValueFrom(this.campaignsApi.getCampaign(this.campaignId))
          : new Campaign({ advertiserId: this.advertiserId });
      }
    } catch (err: any) {
    } finally {
      this.isLoading = false;
    }

    // Load location filters - following punch-card pattern
    if (this.isCloning) {
      // When cloning, location filters are already loaded in cloneCampaign()
      this.campaignLocationFilters().setFormValue(this.clonedLocationFilters);
    } else {
      const clf = this.campaignId
        ? await lastValueFrom(this.campaignLocationFiltersApi.getByCampaignId(this.campaignId))
        : new CampaignLocationFilter({ campaignId: this.campaignId, filters: [] });
      this.campaignLocationFilters().setFormValue(clf);
    }

    this.campaignReplay.next(this.campaign);
  }

  private async cloneCampaign() {
    const { campaign, locationFilters } = await this.campaignCloneService.cloneCampaign(this.originalCampaignId, this.advertiserId);

    this.campaign = campaign;
    this.isAdding = true;
    this.campaignId = undefined;
    // Store location filters to be set in setTimeout when viewChild is ready
    this.clonedLocationFilters = locationFilters;
  }

  forceRefresh() {
    const c = this.campaign;
    this.campaign = null;
    this.isLoading = true;
    setTimeout(() => {
      this.campaign = c;
      this.isLoading = false;
    }, 500);
  }


  async onSave() {

    const request = this.campaignBase().getFormValue();
    if (!request) return;

    let strategy = this.campaignStrategy().getFormValue();
    if (!strategy) return;

    request.campaignId = this.campaignId;
    request.advertiserId = this.advertiserId;
    request.strategy = strategy;

    strategy.allowDuplicates = request.allowDuplicates;               // set this prop on campaign and also on strategy (to be fixed later)

    request.campaignLocationFilter = this.campaignLocationFilters().getFormValue();

    strategy = {
      ...strategy,
      title: request.title,
      // startTime: request.startDate,
      // endTime: request.endDate,
    } as StaffCampaignStrategy;

    this.isLoading = true;
    try {
      await lastValueFrom(this.campaignsApi.createCampaign(request));

      this.snackBar.open(`Campaign ${request.title} was saved`, null, { duration: 3000 });
      this.router.navigate(['advertisers', this.advertiserId]);

    } catch (err: any) {
      if (err.status === 400 && err.response)
        new MessageDialogModel(err.response).show(this.dialog);
    } finally {
      this.isLoading = false;
    }
  }

  refAssignObj(ref: any, obj: any) {
    if (!ref) return;
    for (const key of Object.keys(ref))
      delete ref[key];

    if (!obj) return;
    for (const key of Object.keys(obj))
      ref[key] = obj[key];
  }

}
